import { UUID } from "@bitory-gmbh/nidavellir";
import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { backendApi } from "@/services/backend/api";
import { PricingByArea } from "../types/pricing-by-area";
import { updateCache } from "@/services/backend/_lib/update-cache";

const pricingByAreaApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsPricingByAreaPut: build.mutation<PricingByArea, PricingByArea>({
      query: (request) => ({
        url: "/vbs/pricing-by-area",
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsPricingByArea", id: request.id },
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsConfirmation",
      ],
    }),
    vbsPricingByAreaShow: build.query<PricingByArea, ShowRequest>({
      query: (request) => ({
        url: "/vbs/pricing-by-area",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsPricingByArea", id: result.id }] : [],
    }),
    vbsPricingByAreaList: build.query<List, Partial<ListRequest>>({
      query: (request) => ({
        url: "/vbs/pricing-by-area/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsPricingByArea"),
    }),
    vbsPricingByAreaUpdateUseCalculation: build.mutation<
      PricingByArea,
      UpdateUseCalculationRequest
    >({
      query: (request) => ({
        url: "/vbs/pricing-by-area/use-calculation",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsPricingByArea", id: request.id },
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsConfirmation",
      ],
    }),
    vbsPricingByAreaDelete: build.mutation<void, DeleteRequest>({
      query: (request) => ({
        url: "/vbs/pricing-by-area",
        method: "DELETE",
        params: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsPricingByArea", id: request.id },
        "VbsPricingByArea",
      ],
    }),
  }),
});

export const {
  useVbsPricingByAreaPutMutation,
  useVbsPricingByAreaShowQuery,
  useVbsPricingByAreaListQuery,
  useVbsPricingByAreaUpdateUseCalculationMutation,
  useVbsPricingByAreaDeleteMutation,
} = pricingByAreaApi;

export const updateVbsPricingByAreaCache = updateCache(
  pricingByAreaApi,
  "vbsPricingByAreaShow",
);

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  company?: Filter<UUID>;
  branch?: Filter<UUID>;
  limit?: number;
  offset?: number;
}

export interface List {
  items: PricingByArea[];
  count: number;
}

export interface UpdateUseCalculationRequest {
  id: UUID;
  useCalculation: boolean;
}

export interface DeleteRequest {
  id: UUID;
}

// Optimistically patch the vbsPricingByAreaShow cache entry.
// Dispatch this action for immediate UI updates before the
// debounced mutations fire.
export const updatePricingByAreaCache = (data: PricingByArea) =>
  pricingByAreaApi.util.updateQueryData(
    "vbsPricingByAreaShow",
    { id: data.id },
    () => data,
  );
